import { motion } from "framer-motion";
import { Check, Star } from "lucide-react";
import { pricingTiers } from "./data";
import { cn } from "@/lib/utils";

export default function PricingSection({ onCta }: { onCta: (tier: string) => void }) {
  return (
    <section id="pricing" className="py-24 border-t border-border scroll-mt-20">
      <div className="container mx-auto px-4 laptop:px-6 desktop:px-6">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-4">pricing</p>
          <h2 className="font-funnel font-bold text-3xl tablet:text-4xl laptop:text-5xl desktop:text-5xl text-foreground mb-4">
            start free. pay when it gets{" "}
            <span className="font-cormorant italic font-normal">serious.</span>
          </h2>
          <p className="text-muted-foreground text-lg">
            Explore ideas for nothing. Pay once you want complete, editable plans, or hand the whole
            thing over.
          </p>
        </div>

        <div className="grid mobile:grid-cols-1 tablet:grid-cols-1 laptop:grid-cols-3 desktop:grid-cols-3 gap-6">
          {pricingTiers.map((tier, i) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className={cn(
                "rounded-2xl border p-8 flex flex-col bg-card",
                tier.popular ? "border-primary" : "border-border"
              )}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-funnel font-bold text-2xl text-foreground">{tier.name}</h3>
                {tier.popular && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-[11px] text-secondary-foreground">
                    <Star size={11} /> most popular
                  </span>
                )}
              </div>

              <div className="mb-8">
                <span className="font-funnel font-bold text-4xl text-foreground">{tier.price}</span>
                {tier.sub && <span className="text-muted-foreground text-base ml-2">{tier.sub}</span>}
              </div>

              <ul className="space-y-3 mb-8 flex-1">
                {tier.features.map((f) => (
                  <li key={f} className="flex gap-3 text-foreground/90">
                    <Check size={17} className="text-primary shrink-0 mt-1" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                onClick={() => onCta(tier.name)}
                className={cn(
                  "w-full rounded-full py-3 text-base transition-colors",
                  tier.popular
                    ? "bg-primary text-primary-foreground hover:bg-primary/90"
                    : "border border-border text-foreground hover:border-primary/60"
                )}
              >
                {tier.cta}
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-8 text-sm text-muted-foreground max-w-2xl">
          For fully managed offsites, qwip may charge a percentage of event spend with a minimum
          project fee.
        </p>
      </div>
    </section>
  );
}
